<template>
    <div class="sub_contents">
        <div class="cont_center">
            <h1 class="title_page">공지 사항</h1>

            <p class="sub_descript">
                하이비타민의 새로운 소식들을 확인하실 수 있습니다.
            </p>

            <!-- 검색 -->
            <div class="sub_line_box">
                <div class="input_area notice">
                    <InputText :placeholder="placeholderNotice" v-model="noticeNameInput" @input="updateNoticeName"
                        @keyup.enter="search" />
                    <button type="button" @click="search" class="btn_type1">검색</button>
                </div>
            </div>
            <!-- //검색 -->

            <div class="notice_list_area">
                <!-- 리스트 -->
                <TableList>
                    <template #col>
                        <col style="width: 12.42%;" />
                        <col />
                        <col style="width: 15.62%;" />
                        <col style="width: 12.5%;" />
                    </template>
                    <template #header>
                        <th>No.</th>
                        <th>제 목</th>
                        <th>등록일</th>
                        <th>조회수</th>
                    </template>
                    <template #notice>
                        <!-- 공지사항 (upperYn === 'Y')만 표시 -->
                        <tr v-for="(item, index) in filteredNoticeList" :key="`notice-${item.noticeSeq || index}`"
                            class="notice">
                            <td class="notice">
                                <span class="bedge_notice">공지</span>
                            </td>
                            <td class="t_left title">
                                <a href="#" @click.prevent="routerLink(index, 'notice')">{{ item.noticeTitle }}</a>
                            </td>
                            <td>{{ formatDateTime(item.regDt) }}</td>
                            <td><span class="txt_hit">조회수 :</span> {{ item.noticeCnt || 0 }}</td>
                        </tr>
                    </template>
                    <template #list>
                        <!-- 일반 공지사항 (upperYn === 'N')만 표시 -->
                        <tr v-for="(item, index) in filteredGeneralList" :key="`general-${item.noticeSeq || index}`">
                            <td class="notice_num">{{ item.noticeNo }}</td>
                            <td class="t_left title">
                                <a href="#" @click.prevent="routerLink(index, 'general')">{{ item.noticeTitle }}</a>
                                <span class="bedge_basic" v-if="item.newYn === 'Y'">NEW</span>
                            </td>
                            <td>{{ formatDateTime(item.regDt) }}</td>
                            <td><span class="txt_hit">조회수 :</span> {{ item.noticeCnt || 0 }}</td>
                        </tr>
                    </template>
                </TableList>
                <!-- //리스트 -->

                <!-- 페이징 -->
                <PagingCom v-if="total > 0" :currentPage="currentPage" :pageSize="pageSize" :totalItems="total"
                    @pageChange="onPageChange" />
                <!-- //페이징 -->

                <!-- 데이터 없음 -->
                <div v-if="noticeList.length === 0 && !noticeData.isLoading.value" class="no_data_area">
                    <p>등록된 공지사항이 없습니다.</p>
                </div>

                <!-- 로딩 상태 -->
                <div v-if="noticeData.isLoading.value" class="loading_area">
                    <p>공지사항을 불러오는 중...</p>
                </div>

                <!-- 에러 상태 -->
                <div v-if="noticeData.isError.value" class="error_area">
                    <p>공지사항을 불러오는 중 오류가 발생했습니다.</p>
                    <button @click="noticeData.refetch()" class="btn_retry">다시 시도</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InputText from '@/components/InputText'
import TableList from '@/components/TableList'
import PagingCom from '@/components/PagingCom'
import { ref, watch, onMounted, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getAxios } from '@/lib/AxiosCall';
import { useRouter } from "vue-router";

export default {
    components: {
        InputText,
        TableList,
        PagingCom
    },
    data() {
        return {
            placeholderNotice: '공지사항 제목을 입력해 주세요.',
            noticeNameInput: '' // 검색 입력값 추가
        }
    },
    setup() {
        const router = useRouter();
        const currentPage = ref(1);
        const pageSize = ref(10);
        const total = ref(0);
        const noticeList = ref([]);

        const noticeReqData = ref({
            page: currentPage.value,
            size: pageSize.value,
            title: '',
            brandUuid: sessionStorage.getItem('brandUuid')
        });

        // 공지사항과 일반 목록 분리
        const filteredNoticeList = computed(() => {
            return noticeList.value.filter(item => item.upperYn === 'Y');
        });

        const filteredGeneralList = computed(() => {
            return noticeList.value.filter(item => item.upperYn === 'N');
        });

        // useQuery 설정
        const noticeData = useQuery({
            queryKey: ['get-notice-list', noticeReqData.value],
            queryFn: async () => {
                console.log('공지사항 API 호출:', noticeReqData.value);
                const result = await getAxios("/hvm/v1/help/notice", noticeReqData.value);
                console.log('공지사항 API 응답:', result);
                return result;
            },
            enabled: true, // 자동 실행 활성화
            retry: 3,
            retryDelay: 1000,
        });

        // watch를 통한 데이터 처리
        watch(
            () => noticeData.data.value,
            (newData) => {
                console.log('공지사항 데이터 watch 트리거:', newData);

                if (newData && newData.page && newData.page.list) {
                    // 페이지 이동 시 기존 목록이 남아있지 않도록 항상 교체 방식으로 설정
                    noticeList.value = newData.page.list; // replace instead of append
                    total.value = newData.page.total;

                    console.log('공지사항 리스트 업데이트(교체):', noticeList.value);
                    console.log('공지사항:', filteredNoticeList.value);
                    console.log('일반 목록:', filteredGeneralList.value);
                    console.log('총 개수:', total.value);
                } else {
                    console.warn('공지사항 데이터가 없습니다:', newData);
                    noticeList.value = [];
                    total.value = 0;
                }
            },
            { immediate: true } // 즉시 실행
        );

        // 에러 모니터링
        watch(
            () => noticeData.isError.value,
            (isError) => {
                if (isError) {
                    console.error('공지사항 데이터 로드 에러:', noticeData.error.value);
                }
            }
        );

        // 페이지 변경 핸들러
        function onPageChange(page) {
            console.log('페이지 변경:', page);
            // 페이지 변경 시 기존 리스트를 먼저 clear하여 UI에 잔존 데이터가 보이지 않도록 함
            noticeList.value = [];
            currentPage.value = page;
            noticeReqData.value.page = page;
            noticeData.refetch();
        }

        function routerLink(idx, type = 'general') {
            let selectedNotice;

            if (type === 'notice') {
                selectedNotice = filteredNoticeList.value[idx];
            } else {
                selectedNotice = filteredGeneralList.value[idx];
            }

            console.log('선택된 공지사항:', selectedNotice, 'type:', type);

            if (selectedNotice && selectedNotice.noticeSeq) {
                sessionStorage.setItem('noticeSeq', selectedNotice.noticeSeq);
                router.push("/HFO_CS_002/" + selectedNotice.noticeSeq);
            } else {
                console.error('공지사항 정보가 없습니다:', selectedNotice);
            }
        }

        // 날짜시간 포맷팅 함수
        function formatDateTime(dateTime) {
            if (!dateTime) return '';

            try {
                // "2025-05-30 11:15:07" -> "2025-05-30 11:15"
                const parts = dateTime.split(' ');
                if (parts.length === 2) {
                    const datePart = parts[0];
                    const timePart = parts[1];
                    const timeWithoutSeconds = timePart.split(':').slice(0, 2).join(':');
                    return `${datePart} ${timeWithoutSeconds}`;
                }
                return dateTime;
            } catch (error) {
                console.error('날짜 포맷팅 오류:', error);
                return dateTime;
            }
        }

        // 일반 공지사항 번호 계산 함수
        function getNoticeNumber(index) {
            const generalCount = filteredGeneralList.value.length;
            return generalCount - index;
        }

        return {
            noticeData,
            noticeList,
            filteredNoticeList,
            filteredGeneralList,
            currentPage,
            pageSize,
            total,
            routerLink,
            noticeReqData,
            onPageChange,
            formatDateTime,
            getNoticeNumber
        }
    },
    mounted() {
        console.log('HFO_CS_001 컴포넌트 마운트됨');
        console.log('브랜드 UUID:', sessionStorage.getItem('brandUuid'));
        console.log('초기 요청 데이터:', this.noticeReqData);

        // 마운트 후 데이터 상태 확인
        if (!this.noticeData.data && !this.noticeData.isLoading) {
            console.log('수동 refetch 실행');
            this.noticeData.refetch();
        }
    },
    methods: {
        updateNoticeName() {
            console.log('검색어 업데이트:', this.noticeNameInput);
            this.noticeReqData.title = this.noticeNameInput;
        },
        search() {
            console.log('검색 실행:', this.noticeReqData);
            // 검색 시작 시 기존 리스트를 제거하고 검색 결과로 교체
            if (this.noticeList && typeof this.noticeList === 'object' && 'value' in this.noticeList) {
                this.noticeList.value = [];
            }
            this.currentPage = 1;
            this.noticeReqData.page = this.currentPage;
            this.noticeData.refetch();
        }
    }
}
</script>

<style lang="scss" scoped>
.loading_area,
.error_area,
.no_data_area {
    text-align: center;
    padding: 40px 20px;

    p {
        color: #666;
        margin-bottom: 20px;
    }

    .btn_retry {
        padding: 8px 16px;
        background: #007bff;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;

        &:hover {
            background: #0056b3;
        }
    }
}
</style>